import Immutable from "immutable";
import React, { memo, useMemo } from "react";
import AppConstants from "../../constants/AppConstants";
import {
  FORM_FIELD_CHECKBOX_HEIGHTS,
  FORM_FIELD_DATA_TYPE_EMAIL,
  FORM_FIELD_INPUT_HEIGHTS,
  FORM_FIELD_MULTILINE_HEIGHTS,
  FORM_FIELD_WIDTHS,
} from "../../constants/FormFieldConstants.mjs";
import ModelFactory from "../../models/ModelFactory";
import { generateFontsTagsFromDesignContext } from "../../utils/FontUtils";
import FormField from "../renderer/FormField/FormField";
import ThemeStyleFrame, { FRAME_RESET_CSS } from "../ThemeStyleFrame";

const {
  ELEMENT_TYPE_FORM_SELECT,
  ELEMENT_TYPE_FORM_RADIO,
  ELEMENT_TYPE_FORM_CHECKBOX,
  LAYOUT_MOBILE,
} = AppConstants;

const RADIO_OPTIONS = ["Option 1", "Option 2", "Option 3", "Option 4"];

/**
 * A dummy field built like an inserted element, so it resolves the same theme styles as the stage.
 * @param {string} id
 * @param {Object} factoryOptions - `ModelFactory.getFormFieldElement` options.
 * @param {Object} [heights] - Box heights per layout; omitted to size to the content.
 * @param {Object} [content] - Laid over the seeded content.
 * @param {Object} [previewProps] - Widget state to show, e.g. a selected option.
 */
const field = (id, factoryOptions, heights, content = {}, previewProps = undefined) => {
  const element = ModelFactory.getFormFieldElement(factoryOptions);
  return {
    id,
    heights,
    previewProps,
    element: Immutable.fromJS({ ...element, content: { ...element.content, ...content } }),
  };
};

/** The first field is required to show the required mark. */
const PREVIEW_FIELDS = [
  field("text", {}, FORM_FIELD_INPUT_HEIGHTS, {
    label: "Name",
    placeholder: "Your name",
    required: 1,
  }),
  field("email", { dataType: FORM_FIELD_DATA_TYPE_EMAIL }, FORM_FIELD_INPUT_HEIGHTS),
  field("multi-line", { multiLine: true }, FORM_FIELD_MULTILINE_HEIGHTS, {
    label: "Message",
    placeholder: "Tell us more",
  }),
  field("dropdown", { type: ELEMENT_TYPE_FORM_SELECT }, FORM_FIELD_INPUT_HEIGHTS),
  // No box height: sized to its options, so the gap below matches the other fields.
  // Selected, so the Selected / Checked style controls have something to show.
  field(
    "radio",
    { type: ELEMENT_TYPE_FORM_RADIO },
    undefined,
    { options: RADIO_OPTIONS },
    { value: RADIO_OPTIONS[0] },
  ),
  field(
    "checkbox",
    { type: ELEMENT_TYPE_FORM_CHECKBOX },
    FORM_FIELD_CHECKBOX_HEIGHTS,
    {},
    { checked: true },
  ),
];

/**
 * Dummy form fields for one layout, drawn by the stage's `FormField` in an iframe so theme fonts
 * and widgets load as they do on the stage.
 * @param {Object} props
 * @param {import("immutable").Map} props.theme - Theme being edited.
 * @param {string} props.layout - `desktop` or `mobile`.
 * @param {string} props.fieldState - The `FORM_STATE_*` the panel is editing.
 * @param {Function} props.onSelectField - Called with a field's element type when it is clicked.
 * @param {Object} props.designContext - Design context for font tag generation.
 * @returns {React.ReactElement}
 */
const DesignSettingsFormFieldsPreview = ({
  theme,
  layout,
  fieldState,
  onSelectField,
  designContext,
}) => {
  const fonts = theme.get("fonts");
  const fontTags = useMemo(
    () =>
      fonts ? generateFontsTagsFromDesignContext(Object.values(fonts.toJS()), designContext) : [],
    [fonts, designContext],
  );
  const isMobile = layout === LAYOUT_MOBILE;

  return (
    <ThemeStyleFrame id={`form-fields-${layout}-preview`}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: isMobile ? "center" : "flex-start",
          gap: "24px",
          padding: "8px 0",
        }}
      >
        <div style={{ display: "none" }}>
          {fontTags}
          <style type="text/css">{FRAME_RESET_CSS}</style>
        </div>
        {PREVIEW_FIELDS.map(({ id, element, heights, previewProps }) => (
          <div
            key={id}
            data-testid={`form-fields-preview-${layout}-${id}`}
            onClick={() => onSelectField(element.get("type"))}
            style={{
              width: FORM_FIELD_WIDTHS[layout],
              height: heights?.[layout],
              marginLeft: isMobile ? 0 : "24px",
              cursor: "pointer",
            }}
          >
            <FormField
              element={element}
              theme={theme}
              layout={layout}
              fieldState={fieldState}
              previewProps={previewProps}
            />
          </div>
        ))}
      </div>
    </ThemeStyleFrame>
  );
};

// Memoised so section and background changes in the panel don't re-render the widgets.
export default memo(DesignSettingsFormFieldsPreview);
